import React, { ReactNode, createContext, useContext, useMemo } from 'react';

import { omit } from 'helpers/ramda';

import { Columns, SortOrder } from './interface';

function createCtx<A extends unknown | null>() {
  const DeltaTableContext = createContext<A | undefined>(undefined);
  function useDeltaTableContext() {
    const c = useContext(DeltaTableContext);
    if (c === undefined) {
      throw new Error('useDeltaTableContext must be inside a Provider with a value');
    }
    return c;
  }
  return [useDeltaTableContext, DeltaTableContext.Provider] as const;
}

// TODO: Improve with generic type
const [useDeltaTable, DeltaTableProvider] = createCtx<{
  columns: Array<Columns<any>>;
  data: Record<string, any>;
  sortOrder?: SortOrder;
  autoSizeAllColumns?: boolean;
  resizeColumnsToFit?: boolean;
}>();

interface ProviderProps<T> {
  children: ReactNode;
  columns: Array<Columns<T>>;
  data: T[];
  sortOrder?: SortOrder;
  autoSizeAllColumns?: boolean;
  resizeColumnsToFit?: boolean;
}

const TableProvider = <T,>({
  columns,
  data,
  sortOrder,
  children,
  autoSizeAllColumns,
  resizeColumnsToFit,
}: ProviderProps<T>) => {
  const value = useMemo(() => {
    let clonedCol = [...columns];
    if (resizeColumnsToFit) {
      clonedCol = clonedCol.map((column: Columns<T>) => omit(['width'], column));
    }
    if (autoSizeAllColumns) {
      clonedCol = [...columns];
    }
    return {
      columns: clonedCol,
      data,
      sortOrder,
      autoSizeAllColumns,
      resizeColumnsToFit,
    };
  }, [sortOrder, columns, data, autoSizeAllColumns, resizeColumnsToFit]);

  return <DeltaTableProvider value={value}>{children}</DeltaTableProvider>;
};

export { useDeltaTable, createCtx };
export default TableProvider;
